import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { AppHeader } from "@/components/AppHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Plus, Sparkles, Loader2, Trash2, BarChart3, Upload, CalendarIcon, FileText, ChevronDown, ChevronUp, Check, Copy } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip as UiTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { computeSnapshotStats } from "@/lib/snapshot-stats";

const auditionsHead = () => ({
  meta: [
    { title: "Auditions — Track submissions & callbacks | RoleBoard" },
    {
      name: "description",
      content:
        "Log audition submissions, callbacks, formats, and outcomes in one private workspace built for working actors.",
    },
    { property: "og:title", content: "Auditions — RoleBoard" },
    {
      property: "og:description",
      content:
        "Track every audition, callback, and outcome in your private RoleBoard workspace.",
    },
    { property: "og:url", content: "https://roleboard.app/auditions" },
  ],
  links: [{ rel: "canonical", href: "https://roleboard.app/auditions" }],
});
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { CompanyLogo } from "@/components/CompanyLogo";
import { FormatBadge, getFormatHue } from "@/components/FormatBadge";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Cell,
  PieChart,
  Pie,
  Legend,
  LineChart,
  Line,
  CartesianGrid,
  Area,
  AreaChart,
} from "recharts";
import { ImportAuditionsDialog } from "@/components/auditions/ImportAuditionsDialog";

export const Route = createFileRoute("/auditions")({
  head: auditionsHead,
  validateSearch: (search: Record<string, unknown>): { prefill?: string } => {
    const p = search.prefill;
    return typeof p === "string" && p.length > 0 ? { prefill: p } : {};
  },
  component: AuditionsPage,
});

type Conflict = {
  booked_brand: string;
  new_brand: string;
  category: string;
  confidence: "likely" | "possible";
  reason: string;
  booked_audition?: {
    project_name: string;
    role_name: string;
    audition_date: string | null;
    company_name: string;
  } | null;
};


const PROJECT_FORMATS = [
  "Film (Feature)",
  "TV",
  "Commercial",
  "Short Film",
  "Theatre",
  "Voiceover",
] as const;
const ROLE_TYPES = [
  "Lead/Regular",
  "Supporting",
  "Recurring",
  "Day Player",
  "Background",
] as const;
const TRAITS = [
  "Confident",
  "Cocky",
  "Nervous",
  "Comedic",
  "Dramatic",
  "Aggressive",
  "Friendly",
  "Awkward",
] as const;
const AGE_RANGES = [
  "Under 18",
  "18–21",
  "22–25",
  "26–30",
  "31–35",
  "36–45",
  "46+",
] as const;
// Legacy → new mapping for older auditions logged with broader ranges.
const LEGACY_AGE_MAP: Record<string, string> = {
  "18–25": "22–25",
  "25–35": "26–30",
  "35+": "36–45",
};
function normalizeAgeRange(v: string): string {
  return LEGACY_AGE_MAP[v] ?? v;
}
function bucketAgeRange(v: string | null | undefined): string | null {
  if (!v) return null;
  const s = String(v).trim();
  if (!s) return null;
  const m = s.match(/\d+/);
  if (!m) return null;
  const n = parseInt(m[0], 10);
  if (!Number.isFinite(n)) return null;
  if (n < 18) return "Under 18";
  if (n <= 21) return "18-21";
  if (n <= 25) return "22-25";
  if (n <= 30) return "26-30";
  if (n <= 35) return "31-35";
  if (n <= 40) return "36-40";
  return "40+";
}
const GENRES = [
  "Romance",
  "Horror",
  "Comedy",
  "Drama",
  "Thriller",
  "Action",
  "Sci-Fi",
  "Fantasy",
  "Western",
  "Documentary",
  "Animation",
] as const;
const CASTING_PLATFORMS = [
  "Casting Networks",
  "Showcast",
  "Altai",
  "Talent Web",
  "Actors Access",
  "Spotlight",
] as const;
const SUBMISSION_SOURCES = [
  "Agent",
  "Self Submission",
  "Casting Platform",
  "Referral",
] as const;
const AUDITION_FORMATS = ["Self Tape", "In-Person", "Zoom"] as const;
const OUTCOMES = ["Not Yet Known", "Upcoming", "Callback", "Booked", "No Response"] as const;

type Audition = {
  id: string;
  created_at: string;
  project_name: string;
  role_name: string;
  role_type: string;
  traits: string[];
  age_range: string;
  outcome: string | null;
  project_format: string | null;
  company_name: string | null;
  casting_director: string | null;
  submission_source: string | null;
  audition_format: string | null;
  audition_date: string | null;
  callback_date?: string | null;
  genres?: string[] | null;
};

const auditionSchema = z.object({
  project_name: z.string().trim().min(1, "Project name required").max(120),
  role_name: z.string().trim().min(1, "Role required").max(120),
  audition_date: z.string().min(1, "Audition date required"),
  role_type: z.string().optional(),
  traits: z.array(z.string()).max(20).optional(),
  age_range: z.string().optional(),
  outcome: z.string().optional(),
  project_format: z.string().optional(),
  company_name: z.string().max(120).optional(),
  casting_director: z.string().max(120).optional(),
  submission_source: z.string().optional(),
  audition_format: z.string().optional(),
  casting_platform: z.string().max(120).optional(),
  genres: z.array(z.string()).max(10).optional(),
});

type Prefill = Partial<{
  project_name: string;
  role_name: string;
  audition_date: string;
  audition_format: string;
}>;

function isFutureDate(dateStr: string | null | undefined): boolean {
  if (!dateStr) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr);
  d.setHours(0, 0, 0, 0);
  return d > today;
}

function effectiveOutcome(outcome: string | null, dateStr: string | null): string {
  if (isFutureDate(dateStr) && (!outcome || outcome === "Not Yet Known")) {
    return "Upcoming";
  }
  return outcome || "Submitted";
}

function outcomePillClass(outcome: string | null, dateStr: string | null): string {
  const eff = effectiveOutcome(outcome, dateStr);
  if (eff === "Booked") return "bg-success/10 text-success border-success/30";
  if (eff === "Callback") return "bg-primary/10 text-primary border-primary/30";
  if (eff === "Upcoming")
    return "bg-transparent text-muted-foreground border-dashed border-border";
  return "bg-muted text-muted-foreground border-border";
}

function outcomeLabel(outcome: string | null, dateStr: string | null): string {
  return effectiveOutcome(outcome, dateStr);
}

const MOMENTUM_MESSAGES = [
  "You've been active this month.",
  "Momentum builds consistency.",
  "Every audition moves the needle.",
  "Your career is in motion.",
  "Small steps compound over time.",
];

function MomentumSection({
  auditions,
  onAdd,
}: {
  auditions: Audition[];
  onAdd: () => void;
}) {
  const [expanded, setExpanded] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem("momentum-expanded") === "1";
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    sessionStorage.setItem("momentum-expanded", expanded ? "1" : "0");
  }, [expanded]);

  const message = useMemo(
    () => MOMENTUM_MESSAGES[Math.floor(Math.random() * MOMENTUM_MESSAGES.length)],
    [],
  );

  if (auditions.length === 0) {
    return (
      <Card className="p-6 mb-6 text-center animate-fade-in">
        <h2 className="text-base font-semibold mb-1">Your momentum starts here</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Every audition moves the needle.
        </p>
        <Button onClick={onAdd}>
          <Plus className="size-4" />
          Log your first audition
        </Button>
      </Card>
    );
  }

  const today = new Date(new Date().toDateString());
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const callbacks = auditions.filter(
    (a) => a.outcome === "Callback" || a.outcome === "Booked",
  ).length;
  const upcoming = auditions.filter(
    (a) => a.audition_date && new Date(a.audition_date) >= today,
  ).length;
  const thisMonth = auditions.filter(
    (a) => a.created_at && new Date(a.created_at) >= startOfMonth,
  ).length;
  const logged = auditions.length;

  const summary = `${logged} audition${logged === 1 ? "" : "s"} logged • ${callbacks} callback${callbacks === 1 ? "" : "s"} • ${upcoming} upcoming`;

  const stats = [
    { label: "Logged", value: logged },
    { label: "Callbacks", value: callbacks },
    { label: "Upcoming", value: upcoming },
    { label: "This month", value: thisMonth },
  ];

  const recent = auditions.slice(0, 4);

  return (
    <Card
      className="momentum-card relative mb-6 overflow-hidden animate-fade-in border-border/70"
      data-expanded={expanded ? "true" : "false"}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        e.currentTarget.style.setProperty("--glow-x", `${e.clientX - r.left}px`);
        e.currentTarget.style.setProperty("--glow-y", `${e.clientY - r.top}px`);
      }}
    >
      <span aria-hidden className="momentum-glow" />
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="relative w-full flex items-center justify-between gap-3 px-4 md:px-5 py-3 text-left hover:bg-muted/40 transition-colors"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <h2 className="text-sm font-semibold tracking-tight">Momentum</h2>
              <span className="hidden sm:inline text-xs text-muted-foreground truncate">
                {message}
              </span>
            </div>
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {summary}
            </p>
          </div>
        </div>
        <span className="shrink-0 text-xs text-muted-foreground">
          {expanded ? "Collapse" : "Expand momentum"}
        </span>
      </button>

      {expanded && (
        <div className="px-4 md:px-5 pb-4 pt-1 animate-fade-in">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            {stats.map((s) => (
              <div
                key={s.label}
                className="rounded-lg border border-border/60 bg-background/50 px-3 py-2 transition-colors hover:border-primary/40"
              >
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                  {s.label}
                </p>
                <p className="text-xl font-semibold tracking-tight mt-0.5">
                  {s.value}
                </p>
              </div>
            ))}
          </div>

          <div className="space-y-1.5">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-1">
              Recent activity
            </p>
            {recent.map((a) => {
              const pillClass = outcomePillClass(a.outcome, a.audition_date);
              const pillLabel = outcomeLabel(a.outcome, a.audition_date);
              return (
                <div
                  key={a.id}
                  className="flex items-center justify-between gap-3 rounded-md border border-border/60 bg-background/40 px-3 py-1.5 transition-colors hover:bg-muted/30"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <p className="text-sm font-medium truncate">
                        {a.project_name}
                      </p>
                      <span className="text-xs text-muted-foreground truncate">
                        {a.role_name}
                      </span>
                    </div>
                  </div>
                  <span
                    className={`shrink-0 inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-medium ${pillClass}`}
                  >
                    {pillLabel}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}

function AuditionsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { prefill } = Route.useSearch();

  useEffect(() => {
    if (!authLoading && !user) navigate({ to: "/login" });
  }, [user, authLoading, navigate]);

  const [auditions, setAuditions] = useState<Audition[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"add" | "history" | "insights">(
    prefill ? "add" : "history",
  );
  const [firstName, setFirstName] = useState<string | null>(null);
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const [conflictOpen, setConflictOpen] = useState(false);
  const [copiedConflictIdx, setCopiedConflictIdx] = useState<number | null>(null);

  function closeConflictModal() {
    setConflictOpen(false);
    setConflicts([]);
    setCopiedConflictIdx(null);
  }

  async function copyConflictToEmail(c: Conflict, idx: number) {
    const message = `Hi [agent name],

Just wanted to flag a potential commercial conflict. I've just been submitted for ${c.new_brand} (${c.category}) and I'm currently booked with ${c.booked_brand}. Can you check if I'm clear to audition?

Thanks,
${firstName ?? "[your name]"}`;
    try {
      await navigator.clipboard.writeText(message);
      setCopiedConflictIdx(idx);
      toast.success("Copied to clipboard");
      setTimeout(() => {
        closeConflictModal();
      }, 900);
    } catch {
      toast.error("Couldn't copy to clipboard");
    }
  }


  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("first_name")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data }) => setFirstName(data?.first_name ?? null));
  }, [user]);

  const prefillData: Prefill = useMemo(() => {
    if (!prefill) return {};
    try {
      return JSON.parse(atob(prefill)) as Prefill;
    } catch {
      return {};
    }
  }, [prefill]);

  async function load() {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("auditions")
      .select("*")
      .order("audition_date", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false });
    if (error) toast.error("Couldn't load auditions. Please try again.");
    else setAuditions((data as Audition[]) ?? []);
    setLoading(false);
  }

  useEffect(() => {
    if (user) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const welcomeMessage = firstName
    ? `Welcome back, ${firstName}.`
    : "Welcome back.";

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-background grid place-items-center">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <AppHeader />
      <main className="container max-w-5xl mx-auto px-5 md:px-8 py-8 md:py-12">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
            Auditions
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {welcomeMessage}
          </p>
        </div>

        {!loading && (
          <MomentumSection
            auditions={auditions}
            onAdd={() => setTab("add")}
          />
        )}

        <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
          <TabsList>
            <TabsTrigger
              value="add"
              className="text-primary hover:text-primary data-[state=active]:text-primary"
            >
              <Plus className="size-4 mr-1.5" />
              Add
            </TabsTrigger>
            <TabsTrigger value="history">
              History
              <span className="ml-1.5 text-xs text-muted-foreground">
                {auditions.length}
              </span>
            </TabsTrigger>
            <TabsTrigger value="insights">
              <Sparkles className="size-4 mr-1.5" />
              Insights
            </TabsTrigger>
          </TabsList>

          <TabsContent value="add" className="mt-6">
            <AddAuditionForm
              prefill={prefillData}
              firstName={firstName}
              onCreated={() => {
                load();
                setTab("history");
                if (prefill) navigate({ to: "/auditions", search: {} as never });
              }}
              onConflicts={(found) => {
                if (found.length > 0) {
                  setConflicts(found);
                  setCopiedConflictIdx(null);
                  setConflictOpen(true);
                }
              }}
            />
          </TabsContent>

          <TabsContent value="history" className="mt-6">
            <HistoryList
              loading={loading}
              auditions={auditions}
              onChanged={load}
              onAdd={() => setTab("add")}
            />
          </TabsContent>

          <TabsContent value="insights" className="mt-6">
            <InsightsTabs auditions={auditions} />
          </TabsContent>
        </Tabs>
      </main>

      <Dialog
        open={conflictOpen}
        onOpenChange={(open) => {
          if (!open) closeConflictModal();
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Potential Commercial Conflict</DialogTitle>
            <DialogDescription>
              We noticed this new commercial brand may overlap with a brand you're
              currently booked with. Worth a quick check before submitting.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
            {conflicts.map((c, idx) => {
              const isLikely = c.confidence === "likely";
              const ba = c.booked_audition;
              return (
                <div
                  key={`${c.booked_brand}-${c.new_brand}-${idx}`}
                  className="rounded-md border border-border bg-muted/20 p-4 space-y-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-sm">
                      <span className="font-medium">{c.new_brand}</span>
                      <span className="mx-1.5 text-muted-foreground">vs</span>
                      <span className="font-medium">{c.booked_brand}</span>
                      {c.category && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {c.category}
                        </div>
                      )}
                    </div>
                    <Badge
                      variant="outline"
                      className={
                        isLikely
                          ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                          : "border-yellow-500/40 bg-yellow-500/10 text-yellow-700 dark:text-yellow-300"
                      }
                    >
                      {isLikely ? "Likely conflict" : "Possible conflict"}
                    </Badge>
                  </div>
                  {c.reason && (
                    <p className="text-sm text-muted-foreground">{c.reason}</p>
                  )}
                  {ba && (
                    <div className="rounded border border-border/60 bg-background/60 p-3 text-xs space-y-1">
                      <div className="uppercase tracking-wider text-[10px] font-semibold text-muted-foreground">
                        Your booked audition
                      </div>
                      {ba.project_name && (
                        <div>
                          <span className="text-muted-foreground">Project: </span>
                          <span className="font-medium text-foreground">
                            {ba.project_name}
                          </span>
                        </div>
                      )}
                      {ba.role_name && (
                        <div>
                          <span className="text-muted-foreground">Role: </span>
                          <span className="font-medium text-foreground">
                            {ba.role_name}
                          </span>
                        </div>
                      )}
                      {ba.audition_date && (
                        <div>
                          <span className="text-muted-foreground">Audition date: </span>
                          <span className="font-medium text-foreground">
                            {ba.audition_date}
                          </span>
                        </div>
                      )}
                      {ba.company_name && (
                        <div>
                          <span className="text-muted-foreground">Company / Network: </span>
                          <span className="font-medium text-foreground">
                            {ba.company_name}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex justify-end pt-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => copyConflictToEmail(c, idx)}
                    >
                      {copiedConflictIdx === idx ? (
                        <><Check className="size-3.5 mr-1.5" /> Copied</>
                      ) : (
                        "Copy to email"
                      )}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={closeConflictModal}>
              Dismiss
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function AddAuditionForm({
  prefill,
  onCreated,
  onConflicts,
}: {
  prefill: Prefill;
  firstName: string | null;
  onCreated: () => void;
  onConflicts: (conflicts: Conflict[]) => void;
}) {
  const { user } = useAuth();
  const today = new Date().toISOString().slice(0, 10);

  const [projectName, setProjectName] = useState(prefill.project_name ?? "");
  const [roleName, setRoleName] = useState(prefill.role_name ?? "");
  const [projectFormat, setProjectFormat] = useState<string>("");
  const [companyName, setCompanyName] = useState("");
  const [castingDirector, setCastingDirector] = useState("");
  const [submissionSource, setSubmissionSource] = useState<string>("");
  const [roleType, setRoleType] = useState<string>("");
  const [traits, setTraits] = useState<string[]>([]);
  const [otherTraitOpen, setOtherTraitOpen] = useState(false);
  const [otherTraitInput, setOtherTraitInput] = useState("");
  const [genres, setGenres] = useState<string[]>([]);
  const [customGenreOpen, setCustomGenreOpen] = useState(false);
  const [customGenreInput, setCustomGenreInput] = useState("");
  const [ageMin, setAgeMin] = useState<string>("");
  const [ageMax, setAgeMax] = useState<string>("");
  const [castingPlatform, setCastingPlatform] = useState<string>("");
  const [castingPlatformOther, setCastingPlatformOther] = useState<string>("");
  const [auditionDate, setAuditionDate] = useState<string>(
    prefill.audition_date ?? today,
  );
  const [auditionFormat, setAuditionFormat] = useState<string>(
    prefill.audition_format ?? "Self Tape",
  );
  const [outcome, setOutcome] = useState<string>("Not Yet Known");
  const [submitting, setSubmitting] = useState(false);




  // --- Brief auto-fill panel ---
  const [briefOpen, setBriefOpen] = useState(false);
  const [briefText, setBriefText] = useState("");
  const [briefLoading, setBriefLoading] = useState(false);
  const [briefError, setBriefError] = useState<string | null>(null);
  const [briefSuccess, setBriefSuccess] = useState(false);

  async function handleAutoFill() {
    if (!briefText.trim()) return;
    setBriefLoading(true);
    setBriefError(null);
    setBriefSuccess(false);
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    try {
      const { data, error } = await supabase.functions.invoke("parse-brief", {
        body: { brief: briefText },
      });
      clearTimeout(timeoutId);
      if (error || !data?.extracted) throw new Error("parse failed");
      const e = data.extracted as Record<string, unknown>;

      const strOr = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : null);
      const numOr = (v: unknown) =>
        typeof v === "number" && Number.isFinite(v) ? Math.round(v) : null;

      if (strOr(e.project_name)) setProjectName(strOr(e.project_name)!);
      if (strOr(e.role)) setRoleName(strOr(e.role)!);
      if (strOr(e.company_network)) setCompanyName(strOr(e.company_network)!);
      if (strOr(e.casting_director)) setCastingDirector(strOr(e.casting_director)!);

      const roleAlias: Record<string, string> = {
        Lead: "Lead/Regular",
        Supporting: "Supporting",
        Guest: "Day Player",
        Background: "Background",
      };
      const rt = strOr(e.role_type);
      if (rt && roleAlias[rt]) setRoleType(roleAlias[rt]);

      const minN = numOr(e.age_range_min);
      const maxN = numOr(e.age_range_max);
      if (minN !== null) setAgeMin(String(minN));
      if (maxN !== null) setAgeMax(String(maxN));

      const cp = strOr(e.casting_platform);
      if (cp && (CASTING_PLATFORMS as readonly string[]).includes(cp)) {
        setCastingPlatform(cp);
      }

      if (Array.isArray(e.character_traits)) {
        const valid = e.character_traits.filter(
          (t): t is string =>
            typeof t === "string" && (TRAITS as readonly string[]).includes(t),
        );
        if (valid.length) setTraits((prev) => Array.from(new Set([...prev, ...valid])));
      }

      const pg = strOr(e.project_genre);
      if (pg && (GENRES as readonly string[]).includes(pg)) {
        setGenres((prev) => (prev.includes(pg) ? prev : [...prev, pg]));
      }

      const ad = strOr(e.audition_date);
      if (ad && /^\d{4}-\d{2}-\d{2}/.test(ad)) setAuditionDate(ad.slice(0, 10));

      const afAlias: Record<string, string> = {
        "Self Tape": "Self Tape",
        "In Person": "In-Person",
        Virtual: "Zoom",
      };
      const af = strOr(e.audition_format);
      if (af && afAlias[af]) setAuditionFormat(afAlias[af]);

      setBriefSuccess(true);
      setBriefOpen(false);
    } catch {
      clearTimeout(timeoutId);
      setBriefError("Couldn't read the brief — please fill in the fields manually.");
    } finally {
      setBriefLoading(false);
    }
  }

  const dateIsFuture = isFutureDate(auditionDate);
  useEffect(() => {
    if (dateIsFuture) {
      setOutcome("Upcoming");
    } else if (outcome === "Upcoming") {
      setOutcome("Not Yet Known");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateIsFuture]);

  function toggleTrait(t: string) {
    setTraits((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t],
    );
  }
  function toggleGenre(g: string) {
    setGenres((prev) =>
      prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g],
    );
  }
  function addCustomTraits() {
    const parts = otherTraitInput
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parts.length) return;
    setTraits((prev) => Array.from(new Set([...prev, ...parts])));
    setOtherTraitInput("");
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!user) return;
    const minStr = ageMin.trim();
    const maxStr = ageMax.trim();
    let ageRangeValue = "";
    if (minStr && maxStr) ageRangeValue = `${minStr}-${maxStr}`;
    else if (minStr) ageRangeValue = minStr;
    else if (maxStr) ageRangeValue = maxStr;

    const allGenres = customGenreInput.trim()
      ? Array.from(new Set([...genres, customGenreInput.trim()]))
      : genres;

    const resolvedPlatform =
      castingPlatform === "Other"
        ? castingPlatformOther.trim() || undefined
        : castingPlatform || undefined;

    const parsed = auditionSchema.safeParse({
      project_name: projectName,
      role_name: roleName,
      role_type: roleType,
      traits,
      age_range: ageRangeValue,
      outcome,
      project_format: projectFormat || undefined,
      company_name: companyName || undefined,
      casting_director: castingDirector || undefined,
      submission_source: submissionSource || undefined,
      audition_format: auditionFormat || undefined,
      audition_date: auditionDate || undefined,
      casting_platform: resolvedPlatform,
      genres: allGenres,
    });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("auditions").insert({
      user_id: user.id,
      project_name: parsed.data.project_name,
      role_name: parsed.data.role_name,
      role_type: parsed.data.role_type || "Supporting",
      traits: parsed.data.traits ?? [],
      age_range: parsed.data.age_range ? parsed.data.age_range : null,
      outcome:
        parsed.data.outcome &&
        parsed.data.outcome !== "Not Yet Known" &&
        parsed.data.outcome !== "Upcoming"
          ? parsed.data.outcome
          : null,
      project_format: parsed.data.project_format ?? null,
      company_name: parsed.data.company_name ?? null,
      casting_director: parsed.data.casting_director ?? null,
      submission_source: parsed.data.submission_source ?? null,
      audition_format: parsed.data.audition_format ?? null,
      audition_date: parsed.data.audition_date ?? null,
      casting_platform: parsed.data.casting_platform ?? null,
      genres: parsed.data.genres ?? [],
    });
    setSubmitting(false);
    if (error) {
      toast.error("Couldn't save audition. Please try again.");
      return;
    }
    toast.success("Audition logged");

    // Fire conflict check in the background — do not block save/close flow.
    const newBrand = (parsed.data.company_name ?? "").trim();
    const shouldCheck =
      parsed.data.project_format === "Commercial" && !!newBrand;
    const userId = user.id;
    onCreated();

    if (shouldCheck) {
      (async () => {
        try {
          const { data: booked } = await supabase
            .from("auditions")
            .select("company_name, project_name, role_name, audition_date")
            .eq("user_id", userId)
            .eq("project_format", "Commercial")
            .eq("outcome", "Booked");
          const bookedAuditions = (booked ?? [])
            .map((b) => ({
              company_name: (b.company_name ?? "").trim(),
              project_name: b.project_name ?? "",
              role_name: b.role_name ?? "",
              audition_date: b.audition_date ?? null,
            }))
            .filter(
              (b) =>
                b.company_name &&
                b.company_name.toLowerCase() !== newBrand.toLowerCase(),
            );
          if (bookedAuditions.length === 0) return;
          const { data: cdata } = await supabase.functions.invoke(
            "check-commercial-conflicts",
            { body: { new_brand: newBrand, booked_auditions: bookedAuditions } },
          );
          const found: Conflict[] = Array.isArray(cdata?.conflicts)
            ? cdata.conflicts
            : [];
          if (found.length > 0) onConflicts(found);
        } catch (err) {
          console.error("conflict check failed", err);
          // Fail silently — do not surface to the user.
        }
      })();
    }
  }



  return (
    <Card className="p-6">
      <form onSubmit={onSubmit} className="space-y-8">
        {/* BRIEF AUTO-FILL */}
        <section className="rounded-md border border-border bg-muted/30">
          <button
            type="button"
            onClick={() => {
              setBriefOpen((v) => !v);
              setBriefSuccess(false);
            }}
            className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium hover:bg-muted/50 transition-colors rounded-md"
            aria-expanded={briefOpen}
          >
            <span className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-teal-600 dark:text-teal-400" />
              Paste your brief
            </span>
            {briefOpen ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>
          {briefOpen && (
            <div className="px-4 pb-4 pt-1 space-y-3">
              <Textarea
                value={briefText}
                onChange={(e) => setBriefText(e.target.value)}
                placeholder="Paste your casting brief here and we'll fill in the fields for you..."
                rows={6}
                maxLength={20000}
                disabled={briefLoading}
              />
              <div className="flex items-center gap-3">
                <Button
                  type="button"
                  onClick={handleAutoFill}
                  disabled={briefLoading || !briefText.trim()}
                  className="bg-teal-600 hover:bg-teal-700 text-white"
                >
                  {briefLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Reading brief...
                    </>
                  ) : (
                    "Auto-fill from brief"
                  )}
                </Button>
                {briefError && (
                  <p className="text-xs text-destructive">{briefError}</p>
                )}
              </div>
            </div>
          )}
          {!briefOpen && briefSuccess && (
            <div className="px-4 pb-3 -mt-1 flex items-center gap-2 text-xs text-teal-700 dark:text-teal-400">
              <Check className="h-3.5 w-3.5" />
              Fields pre-filled from your brief — check and adjust as needed.
            </div>
          )}
          {!briefOpen && briefError && !briefSuccess && (
            <div className="px-4 pb-3 -mt-1 text-xs text-destructive">{briefError}</div>
          )}
        </section>

        {/* CORE */}
        <section className="space-y-4">
          <SectionHeader title="Core" />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Project format / medium" optional>
              <Select value={projectFormat} onValueChange={setProjectFormat}>
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {PROJECT_FORMATS.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Project name">
              <Input
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="e.g. Stranger Things"
                maxLength={120}
              />
            </Field>
            <Field label="Role">
              <Input
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                placeholder="e.g. Eddie"
                maxLength={120}
              />
            </Field>
          </div>
        </section>

        {/* SECONDARY */}
        <section className="space-y-4">
          <SectionHeader title="Casting details" />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Company / network" optional>
              <Input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="e.g. Netflix"
                maxLength={120}
              />
            </Field>
            <Field label="Casting director" optional>
              <Input
                value={castingDirector}
                onChange={(e) => setCastingDirector(e.target.value)}
                placeholder="Name"
                maxLength={120}
              />
            </Field>
            <Field label="Submission source" optional>
              <Select value={submissionSource} onValueChange={setSubmissionSource}>
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {SUBMISSION_SOURCES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
        </section>

        {/* ROLE DETAILS */}
        <section className="space-y-4">
          <SectionHeader title="Role" />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Role type" optional>
              <Select value={roleType} onValueChange={setRoleType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_TYPES.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Age range" optional hint="e.g. 20 - 30">
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={120}
                  step={1}
                  placeholder="Min"
                  value={ageMin}
                  onChange={(e) => setAgeMin(e.target.value.replace(/[^0-9]/g, ""))}
                />
                <span className="text-muted-foreground">–</span>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={120}
                  step={1}
                  placeholder="Max"
                  value={ageMax}
                  onChange={(e) => setAgeMax(e.target.value.replace(/[^0-9]/g, ""))}
                />
              </div>
            </Field>
          </div>

          <Field label="Casting platform" optional>
            <Select value={castingPlatform} onValueChange={setCastingPlatform}>
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {CASTING_PLATFORMS.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
                <SelectItem value="Other">Other (please specify)</SelectItem>
              </SelectContent>
            </Select>
            {castingPlatform === "Other" && (
              <Input
                className="mt-2"
                value={castingPlatformOther}
                onChange={(e) => setCastingPlatformOther(e.target.value)}
                placeholder="Specify platform"
                maxLength={120}
              />
            )}
          </Field>

          <Field label="Character traits" optional>
            <div className="flex flex-wrap gap-2">
              {TRAITS.map((t) => {
                const active = traits.includes(t);
                return (
                  <button
                    type="button"
                    key={t}
                    onClick={() => toggleTrait(t)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                      active
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-transparent text-muted-foreground border-border hover:text-foreground hover:border-foreground/40"
                    }`}
                  >
                    {t}
                  </button>
                );
              })}
              {traits
                .filter((t) => !(TRAITS as readonly string[]).includes(t))
                .map((t) => (
                  <button
                    type="button"
                    key={t}
                    onClick={() => toggleTrait(t)}
                    className="px-3 py-1.5 rounded-full text-sm border bg-primary text-primary-foreground border-primary"
                    title="Remove custom trait"
                  >
                    {t} ✕
                  </button>
                ))}
              <button
                type="button"
                onClick={() => setOtherTraitOpen((v) => !v)}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  otherTraitOpen
                    ? "bg-foreground text-background border-foreground"
                    : "bg-transparent text-muted-foreground border-border border-dashed hover:text-foreground hover:border-foreground/40"
                }`}
              >
                + Other
              </button>
            </div>
            {otherTraitOpen && (
              <div className="flex gap-2 mt-2">
                <Input
                  value={otherTraitInput}
                  onChange={(e) => setOtherTraitInput(e.target.value)}
                  placeholder="Add custom trait (comma-separated)"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addCustomTraits();
                    }
                  }}
                  maxLength={60}
                />
                <Button type="button" variant="outline" onClick={addCustomTraits}>
                  Add
                </Button>
              </div>
            )}
          </Field>

          <Field label="Project genre" optional>
            <div className="flex flex-wrap gap-2">
              {GENRES.map((g) => {
                const active = genres.includes(g);
                return (
                  <button
                    type="button"
                    key={g}
                    onClick={() => toggleGenre(g)}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                      active
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-transparent text-muted-foreground border-border hover:text-foreground hover:border-foreground/40"
                    }`}
                  >
                    {g}
                  </button>
                );
              })}
              {genres
                .filter((g) => !(GENRES as readonly string[]).includes(g))
                .map((g) => (
                  <button
                    type="button"
                    key={g}
                    onClick={() => toggleGenre(g)}
                    className="px-3 py-1.5 rounded-full text-sm border bg-primary text-primary-foreground border-primary"
                    title="Remove custom genre"
                  >
                    {g} ✕
                  </button>
                ))}
              <button
                type="button"
                onClick={() => setCustomGenreOpen((v) => !v)}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  customGenreOpen
                    ? "bg-foreground text-background border-foreground"
                    : "bg-transparent text-muted-foreground border-border border-dashed hover:text-foreground hover:border-foreground/40"
                }`}
              >
                + Custom
              </button>
            </div>
            {customGenreOpen && (
              <div className="flex gap-2 mt-2">
                <Input
                  value={customGenreInput}
                  onChange={(e) => setCustomGenreInput(e.target.value)}
                  placeholder="Add custom genre"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      const v = customGenreInput.trim();
                      if (v) {
                        setGenres((prev) => Array.from(new Set([...prev, v])));
                        setCustomGenreInput("");
                      }
                    }
                  }}
                  maxLength={60}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const v = customGenreInput.trim();
                    if (!v) return;
                    setGenres((prev) => Array.from(new Set([...prev, v])));
                    setCustomGenreInput("");
                  }}
                >
                  Add
                </Button>
              </div>
            )}
          </Field>
        </section>

        {/* META */}
        <section className="space-y-4">
          <SectionHeader title="Meta" />
          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Audition date">
              <Input
                type="date"
                value={auditionDate}
                onChange={(e) => setAuditionDate(e.target.value)}
              />
            </Field>
            <Field label="Audition format" optional>
              <Select value={auditionFormat} onValueChange={setAuditionFormat}>
                <SelectTrigger>
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {AUDITION_FORMATS.map((f) => (
                    <SelectItem key={f} value={f}>
                      {f}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Outcome" optional>
              <Select value={outcome} onValueChange={setOutcome} disabled={dateIsFuture}>
                <SelectTrigger className={dateIsFuture ? "opacity-60" : undefined}>
                  <SelectValue placeholder="Not yet known" />
                </SelectTrigger>
                <SelectContent>
                  {OUTCOMES.filter((o) => dateIsFuture || o !== "Upcoming").map((o) => (
                    <SelectItem key={o} value={o}>
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {dateIsFuture && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  Locked as Upcoming until the audition date passes.
                </p>
              )}
            </Field>
          </div>
        </section>

        <div className="flex justify-end pt-2 border-t border-border">
          <Button type="submit" size="lg" disabled={submitting}>
            {submitting ? "Saving…" : "Log audition"}
          </Button>
        </div>
      </form>
    </Card>
  );
}


function SectionHeader({ title }: { title: string }) {
  return (
    <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
      {title}
    </h3>
  );
}

function Field({
  label,
  optional,
  hint,
  children,
}: {
  label: string;
  optional?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="flex items-center gap-1.5">
        <span>{label}</span>
        {optional && (
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70 font-normal">
            Optional
          </span>
        )}
      </Label>
      {children}
      {hint && (
        <p className="text-xs text-muted-foreground/70">{hint}</p>
      )}
    </div>
  );
}

function HistoryList({
  loading,
  auditions,
  onChanged,
  onAdd,
}: {
  loading: boolean;
  auditions: Audition[];
  onChanged: () => void;
  onAdd: () => void;
}) {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [outcomeFilter, setOutcomeFilter] = useState<string>("all");
  const [formatFilter, setFormatFilter] = useState<string>("all");
  const [roleTypeFilter, setRoleTypeFilter] = useState<string>("all");
  const [genreFilter, setGenreFilter] = useState<string>("all");
  const [datePreset, setDatePreset] = useState<string>("all");
  const [customYear, setCustomYear] = useState<string>("all");
  const [customMonth, setCustomMonth] = useState<string>("all");
  const [detailId, setDetailId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);

  const sorted = useMemo(() => {
    return [...auditions].sort((a, b) => {
      const da = new Date(a.audition_date ?? a.created_at).getTime();
      const db = new Date(b.audition_date ?? b.created_at).getTime();
      return db - da;
    });
  }, [auditions]);

  const availableYears = useMemo(() => {
    const ys = new Set<number>();
    sorted.forEach((a) => {
      ys.add(new Date(a.audition_date ?? a.created_at).getFullYear());
    });
    return Array.from(ys).sort((a, b) => b - a);
  }, [sorted]);

  const filtered = useMemo(() => {
    const now = new Date();
    const q = searchQuery.trim().toLowerCase();
    return sorted.filter((a) => {
      if (q) {
        const hay = [a.project_name, a.role_name, a.company_name, a.casting_director]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (outcomeFilter !== "all") {
        const eff = effectiveOutcome(a.outcome, a.audition_date);
        if (outcomeFilter === "Not Yet Known") {
          if (a.outcome || isFutureDate(a.audition_date)) return false;
        } else if (eff !== outcomeFilter) {
          return false;
        }
      }
      if (formatFilter !== "all" && a.project_format !== formatFilter) return false;
      if (roleTypeFilter !== "all" && a.role_type !== roleTypeFilter) return false;
      if (genreFilter !== "all" && !(a.genres ?? []).includes(genreFilter)) return false;
      const d = new Date(a.audition_date ?? a.created_at);
      if (datePreset === "thisYear" && d.getFullYear() !== now.getFullYear()) return false;
      if (
        datePreset === "thisMonth" &&
        (d.getFullYear() !== now.getFullYear() || d.getMonth() !== now.getMonth())
      )
        return false;
      if (datePreset === "custom") {
        if (customYear !== "all" && d.getFullYear() !== Number(customYear)) return false;
        if (customMonth !== "all" && d.getMonth() !== Number(customMonth)) return false;
      }
      return true;
    });
  }, [sorted, searchQuery, outcomeFilter, formatFilter, roleTypeFilter, genreFilter, datePreset, customYear, customMonth]);

  const grouped = useMemo(() => {
    const MONTHS = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ];
    const byYear = new Map<number, Map<number, Audition[]>>();
    filtered.forEach((a) => {
      const d = new Date(a.audition_date ?? a.created_at);
      const y = d.getFullYear();
      const m = d.getMonth();
      if (!byYear.has(y)) byYear.set(y, new Map());
      const months = byYear.get(y)!;
      if (!months.has(m)) months.set(m, []);
      months.get(m)!.push(a);
    });
    return Array.from(byYear.entries())
      .sort((a, b) => b[0] - a[0])
      .map(([year, months]) => ({
        year,
        months: Array.from(months.entries())
          .sort((a, b) => b[0] - a[0])
          .map(([m, items]) => ({ month: m, monthLabel: MONTHS[m], items })),
      }));
  }, [filtered]);

  const filtersActive =
    searchQuery.trim() !== "" ||
    outcomeFilter !== "all" ||
    formatFilter !== "all" ||
    roleTypeFilter !== "all" ||
    genreFilter !== "all" ||
    datePreset !== "all";

  function resetFilters() {
    setSearchQuery("");
    setOutcomeFilter("all");
    setFormatFilter("all");
    setRoleTypeFilter("all");
    setGenreFilter("all");
    setDatePreset("all");
    setCustomYear("all");
    setCustomMonth("all");
  }

  const detail = useMemo(
    () => auditions.find((a) => a.id === detailId) ?? null,
    [auditions, detailId],
  );

  async function handleDelete(id: string) {
    const { error } = await supabase.from("auditions").delete().eq("id", id);
    if (error) toast.error("Couldn't delete audition. Please try again.");
    else {
      toast.success("Deleted");
      onChanged();
    }
  }

  const [callbackTarget, setCallbackTarget] = useState<Audition | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Audition | null>(null);

  async function handleOutcomeChange(id: string, value: string) {
    const audition = auditions.find((a) => a.id === id);
    if (!audition) return;
    if (isFutureDate(audition.audition_date)) {
      toast.info("This audition is upcoming. Outcomes unlock once the date passes.");
      return;
    }
    if (value === "Callback") {
      setCallbackTarget(audition);
      return;
    }
    const next = value === "Not Yet Known" || value === "Upcoming" ? null : value;
    const { error } = await supabase
      .from("auditions")
      .update({ outcome: next, callback_date: null })
      .eq("id", id);
    if (error) toast.error("Couldn't update audition. Please try again.");
    else onChanged();
  }

  async function saveCallback(date: string): Promise<boolean> {
    if (!callbackTarget) return false;
    const { error } = await supabase
      .from("auditions")
      .update({ outcome: "Callback", callback_date: date })
      .eq("id", callbackTarget.id);
    if (error) {
      toast.error("Couldn't save callback. Please try again.");
      return false;
    }
    onChanged();
    return true;
  }

  if (loading && auditions.length === 0) {
    return (
      <div className="grid place-items-center py-16">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (auditions.length === 0) {
    return (
      <>
        <Card className="p-10 text-center">
          <p className="text-sm text-muted-foreground">
            No auditions logged yet.
          </p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <Button onClick={onAdd}>
              <Plus className="size-4 mr-1.5" />
              Log your first audition
            </Button>
            <Button variant="outline" onClick={() => setImportOpen(true)}>
              <Upload className="size-4 mr-1.5" />
              Import auditions
            </Button>
          </div>
        </Card>
        <ImportAuditionsDialog
          open={importOpen}
          onOpenChange={setImportOpen}
          onImported={onChanged}
        />
      </>
    );
  }

  const MONTH_OPTIONS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  const renderCard = (a: Audition) => {
    const isUpcoming = isFutureDate(a.audition_date) && !a.outcome;
    const statusControl = isUpcoming ? (
      <span className="inline-flex items-center px-2.5 h-8 rounded-md border border-dashed border-border/70 bg-background/60 text-xs text-muted-foreground">
        Upcoming
      </span>
    ) : (
      <Select value={a.outcome ?? "Not Yet Known"} onValueChange={(v) => handleOutcomeChange(a.id, v)}>
        <SelectTrigger className="h-8 w-full sm:w-[150px] text-xs font-medium border-border/70 bg-background/60 text-foreground/80">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {OUTCOMES.filter((o) => o !== "Upcoming").map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
        </SelectContent>
      </Select>
    );

    return (
      <Card
        key={a.id}
        role="button"
        tabIndex={0}
        onClick={() => setDetailId(a.id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setDetailId(a.id);
          }
        }}
        className="p-4 cursor-pointer transition-colors hover:bg-muted/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring overflow-hidden"
      >
        {/* Top row: identity + (desktop) status/delete */}
        <div className="flex items-start justify-between gap-3 sm:gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0 flex-wrap">
              <CompanyLogo name={a.company_name} size={18} />
              <h3 className="font-semibold tracking-tight truncate text-[15px] min-w-0">{a.project_name}</h3>
              {a.project_format && (
                <FormatBadge format={a.project_format} className="ml-0.5 shrink-0" />
              )}
            </div>
            <div className="mt-1 text-sm text-muted-foreground truncate">
              <span>{a.role_name}</span>
              {a.company_name && (<><span className="mx-1.5 opacity-60">·</span><span>{a.company_name}</span></>)}
            </div>
            <div className="mt-1.5 text-xs text-muted-foreground/80 truncate">
              {a.casting_director && (<><span>CD: {a.casting_director}</span><span className="mx-1.5 opacity-60">·</span></>)}
              {a.audition_format && (<><span>{a.audition_format}</span><span className="mx-1.5 opacity-60">·</span></>)}
              <span>{new Date(a.audition_date ?? a.created_at).toLocaleDateString()}</span>
            </div>
          </div>
          {/* Desktop only: inline status + delete */}
          <div
            className="hidden sm:flex items-center gap-2 shrink-0"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            {statusControl}
            <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(a)} className="text-muted-foreground hover:text-destructive">
              <Trash2 className="size-4" />
            </Button>
          </div>
        </div>

        {/* Mobile only: status + delete in their own row underneath */}
        <div
          className="sm:hidden mt-3 flex items-center gap-2"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <div className="flex-1 min-w-0">{statusControl}</div>
          <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(a)} className="text-muted-foreground hover:text-destructive shrink-0">
            <Trash2 className="size-4" />
          </Button>
        </div>
      </Card>
    );
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2">
        <Input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search auditions…"
          className="flex-1"
          aria-label="Search auditions"
        />
        <Button variant="outline" onClick={() => setImportOpen(true)}>
          <Upload className="size-4 mr-1.5" />
          Import
        </Button>
      </div>
      <ImportAuditionsDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={onChanged}
      />
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Date</Label>
            <Select value={datePreset} onValueChange={setDatePreset}>
              <SelectTrigger className="h-9 w-[140px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All time</SelectItem>
                <SelectItem value="thisYear">This year</SelectItem>
                <SelectItem value="thisMonth">This month</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {datePreset === "custom" && (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Year</Label>
                <Select value={customYear} onValueChange={setCustomYear}>
                  <SelectTrigger className="h-9 w-[110px] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {availableYears.map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Month</Label>
                <Select value={customMonth} onValueChange={setCustomMonth}>
                  <SelectTrigger className="h-9 w-[130px] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    {MONTH_OPTIONS.map((m, i) => (
                      <SelectItem key={m} value={String(i)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Format</Label>
            <Select value={formatFilter} onValueChange={setFormatFilter}>
              <SelectTrigger className="h-9 w-[150px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All formats</SelectItem>
                {PROJECT_FORMATS.map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Role type</Label>
            <Select value={roleTypeFilter} onValueChange={setRoleTypeFilter}>
              <SelectTrigger className="h-9 w-[150px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                {ROLE_TYPES.map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Genre</Label>
            <Select value={genreFilter} onValueChange={setGenreFilter}>
              <SelectTrigger className="h-9 w-[150px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All genres</SelectItem>
                {GENRES.map((g) => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Outcome</Label>
            <Select value={outcomeFilter} onValueChange={setOutcomeFilter}>
              <SelectTrigger className="h-9 w-[150px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All outcomes</SelectItem>
                {OUTCOMES.map((o) => (
                  <SelectItem key={o} value={o}>{o}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {filtersActive && (
            <Button variant="ghost" size="sm" onClick={resetFilters} className="ml-auto text-muted-foreground">
              Clear filters
            </Button>
          )}
        </div>
      </Card>

      {filtered.length === 0 ? (
        <Card className="p-10 text-center">
          <p className="text-sm text-muted-foreground">No auditions match your filters.</p>
          {filtersActive && (
            <Button variant="outline" size="sm" onClick={resetFilters} className="mt-4">
              Clear filters
            </Button>
          )}
        </Card>
      ) : (
        <div className="space-y-8">
          {grouped.map((g) => (
            <section key={g.year} className="space-y-4">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-semibold tracking-tight">{g.year}</h2>
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs text-muted-foreground">
                  {g.months.reduce((n, m) => n + m.items.length, 0)}
                </span>
              </div>
              {g.months.map((m) => (
                <div key={m.month} className="space-y-2">
                  <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold pl-0.5">
                    {m.monthLabel}
                  </h3>
                  <div className="grid gap-3">
                    {m.items.map(renderCard)}
                  </div>
                </div>
              ))}
            </section>
          ))}
        </div>
      )}

      <AuditionDetailDialog
        audition={detail}
        onClose={() => setDetailId(null)}
        onSaved={() => {
          setDetailId(null);
          onChanged();
        }}
        onRequestCallback={(a) => setCallbackTarget(a)}
      />

      <CallbackDialog
        audition={callbackTarget}
        onClose={() => setCallbackTarget(null)}
        onSave={saveCallback}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base font-medium">
              {deleteTarget?.project_name
                ? `Delete "${deleteTarget.project_name}" audition?`
                : "Delete audition?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-muted-foreground leading-relaxed">
              This will permanently remove the audition from your history and calendar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="font-normal">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (deleteTarget) {
                  await handleDelete(deleteTarget.id);
                  setDeleteTarget(null);
                }
              }}
              className="bg-transparent text-destructive border border-destructive/30 hover:bg-destructive/10 hover:text-destructive shadow-none"
            >
              Delete audition
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AuditionDetailDialog({
  audition,
  onClose,
  onSaved,
  onRequestCallback,
}: {
  audition: Audition | null;
  onClose: () => void;
  onSaved: () => void;
  onRequestCallback?: (audition: Audition) => void;
}) {
  const [form, setForm] = useState<Audition | null>(audition);
  const [saving, setSaving] = useState(false);
  const [otherTraitOpen, setOtherTraitOpen] = useState(false);
  const [otherTraitInput, setOtherTraitInput] = useState("");
  const [editAgeMin, setEditAgeMin] = useState<string>("");
  const [editAgeMax, setEditAgeMax] = useState<string>("");
  const [editAgeMinPlaceholder, setEditAgeMinPlaceholder] = useState<string>("Min");

  useEffect(() => {
    setForm(audition);
    const raw = audition?.age_range?.trim() ?? "";
    let min = "";
    let max = "";
    let placeholder = "Min";
    if (raw) {
      const rangeMatch = raw.match(/^(\d+)\s*[-–—]\s*(\d+)$/);
      if (rangeMatch) {
        min = rangeMatch[1];
        max = rangeMatch[2];
      } else if (/^\d+$/.test(raw)) {
        min = raw;
      } else if (/^under\s*(\d+)$/i.test(raw)) {
        max = raw.match(/^under\s*(\d+)$/i)![1];
      } else if (/^(\d+)\s*\+$/.test(raw)) {
        min = raw.match(/^(\d+)\s*\+$/)![1];
      } else {
        placeholder = raw;
      }
    }
    setEditAgeMin(min);
    setEditAgeMax(max);
    setEditAgeMinPlaceholder(placeholder);
  }, [audition]);

  if (!audition || !form) return null;

  function set<K extends keyof Audition>(key: K, value: Audition[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  function toggleTrait(t: string) {
    if (!form) return;
    const has = form.traits.includes(t);
    set("traits", has ? form.traits.filter((x) => x !== t) : [...form.traits, t]);
  }
  function toggleGenre(g: string) {
    if (!form) return;
    const list = form.genres ?? [];
    set("genres", list.includes(g) ? list.filter((x) => x !== g) : [...list, g]);
  }
  function addCustomTraits() {
    if (!form) return;
    const parts = otherTraitInput
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (!parts.length) return;
    set("traits", Array.from(new Set([...form.traits, ...parts])));
    setOtherTraitInput("");
  }

  async function save() {
    if (!form) return;
    const minStr = editAgeMin.trim();
    const maxStr = editAgeMax.trim();
    let ageRangeValue = "";
    if (minStr && maxStr) ageRangeValue = `${minStr}-${maxStr}`;
    else if (minStr) ageRangeValue = minStr;
    else if (maxStr) ageRangeValue = maxStr;

    const parsed = auditionSchema.safeParse({
      project_name: form.project_name,
      role_name: form.role_name,
      role_type: form.role_type,
      traits: form.traits,
      age_range: ageRangeValue,
      outcome: form.outcome ?? "Not Yet Known",
      project_format: form.project_format ?? undefined,
      company_name: form.company_name ?? undefined,
      casting_director: form.casting_director ?? undefined,
      submission_source: form.submission_source ?? undefined,
      audition_format: form.audition_format ?? undefined,
      audition_date: form.audition_date ?? undefined,
      genres: form.genres ?? [],
    });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0].message);
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("auditions")
      .update({
        project_name: parsed.data.project_name,
        role_name: parsed.data.role_name,
        role_type: parsed.data.role_type || "Supporting",
        traits: parsed.data.traits ?? [],
        age_range: parsed.data.age_range || null,
        outcome:
          parsed.data.outcome &&
          parsed.data.outcome !== "Not Yet Known" &&
          parsed.data.outcome !== "Upcoming"
            ? parsed.data.outcome
            : null,
        project_format: parsed.data.project_format ?? null,
        company_name: parsed.data.company_name ?? null,
        casting_director: parsed.data.casting_director ?? null,
        submission_source: parsed.data.submission_source ?? null,
        audition_format: parsed.data.audition_format ?? null,
        audition_date: parsed.data.audition_date ?? null,
        genres: parsed.data.genres ?? [],
      })
      .eq("id", form.id);
    setSaving(false);
    if (error) {
      toast.error("Couldn't save changes. Please try again.");
      return;
    }
    toast.success("Updated");
    const needsCallback =
      parsed.data.outcome === "Callback" && !form.callback_date;
    if (needsCallback && onRequestCallback) {
      onRequestCallback(form);
    }
    onSaved();
  }

  return (
    <Dialog open={!!audition} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {form.project_name || "Untitled"}
            <span className="text-muted-foreground font-normal"> · {form.role_name || "—"}</span>
          </DialogTitle>
          <DialogDescription>
            Edit any field and save your changes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-2">
          <section className="space-y-3">
            <SectionHeader title="Core info" />
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Project format">
                <Select
                  value={form.project_format ?? ""}
                  onValueChange={(v) => set("project_format", v)}
                >
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {PROJECT_FORMATS.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Project name">
                <Input value={form.project_name} onChange={(e) => set("project_name", e.target.value)} maxLength={120} />
              </Field>
              <Field label="Role">
                <Input value={form.role_name} onChange={(e) => set("role_name", e.target.value)} maxLength={120} />
              </Field>
            </div>
          </section>

          <section className="space-y-3">
            <SectionHeader title="Production" />
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Company / network">
                <Input value={form.company_name ?? ""} onChange={(e) => set("company_name", e.target.value)} maxLength={120} />
              </Field>
              <Field label="Casting director">
                <Input value={form.casting_director ?? ""} onChange={(e) => set("casting_director", e.target.value)} maxLength={120} />
              </Field>
            </div>
          </section>

          <section className="space-y-3">
            <SectionHeader title="Role details" />
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Role type">
                <Select value={form.role_type} onValueChange={(v) => set("role_type", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLE_TYPES.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Age range" optional>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={120}
                    step={1}
                    placeholder={editAgeMinPlaceholder}
                    value={editAgeMin}
                    onChange={(e) => setEditAgeMin(e.target.value.replace(/[^0-9]/g, ""))}
                  />
                  <span className="text-muted-foreground">–</span>
                  <Input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    max={120}
                    step={1}
                    placeholder="Max"
                    value={editAgeMax}
                    onChange={(e) => setEditAgeMax(e.target.value.replace(/[^0-9]/g, ""))}
                  />
                </div>
              </Field>
            </div>
            <Field label="Character traits">
              <div className="flex flex-wrap gap-2">
                {TRAITS.map((t) => {
                  const active = form.traits.includes(t);
                  return (
                    <button
                      type="button"
                      key={t}
                      onClick={() => toggleTrait(t)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                        active
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-transparent text-muted-foreground border-border hover:text-foreground hover:border-foreground/40"
                      }`}
                    >
                      {t}
                    </button>
                  );
                })}
                {form.traits
                  .filter((t) => !(TRAITS as readonly string[]).includes(t))
                  .map((t) => (
                    <button
                      type="button"
                      key={t}
                      onClick={() => toggleTrait(t)}
                      className="px-3 py-1.5 rounded-full text-sm border bg-primary text-primary-foreground border-primary"
                    >
                      {t} ✕
                    </button>
                  ))}
                <button
                  type="button"
                  onClick={() => setOtherTraitOpen((v) => !v)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                    otherTraitOpen
                      ? "bg-foreground text-background border-foreground"
                      : "bg-transparent text-muted-foreground border-border border-dashed hover:text-foreground hover:border-foreground/40"
                  }`}
                >
                  + Other
                </button>
              </div>
              {otherTraitOpen && (
                <div className="flex gap-2 mt-2">
                  <Input
                    value={otherTraitInput}
                    onChange={(e) => setOtherTraitInput(e.target.value)}
                    placeholder="Add custom trait (comma-separated)"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addCustomTraits();
                      }
                    }}
                    maxLength={60}
                  />
                  <Button type="button" variant="outline" onClick={addCustomTraits}>
                    Add
                  </Button>
                </div>
              )}
            </Field>
            <Field label="Project genre">
              <div className="flex flex-wrap gap-2">
                {GENRES.map((g) => {
                  const active = (form.genres ?? []).includes(g);
                  return (
                    <button
                      type="button"
                      key={g}
                      onClick={() => toggleGenre(g)}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                        active
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-transparent text-muted-foreground border-border hover:text-foreground hover:border-foreground/40"
                      }`}
                    >
                      {g}
                    </button>
                  );
                })}
              </div>
            </Field>
          </section>

          <section className="space-y-3">
            <SectionHeader title="Meta" />
            <div className="grid sm:grid-cols-3 gap-3">
              <Field label="Audition date">
                <Input
                  type="date"
                  value={form.audition_date ?? ""}
                  onChange={(e) => set("audition_date", e.target.value)}
                />
              </Field>
              <Field label="Audition format">
                <Select
                  value={form.audition_format ?? ""}
                  onValueChange={(v) => set("audition_format", v)}
                >
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {AUDITION_FORMATS.map((f) => (
                      <SelectItem key={f} value={f}>{f}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Submission source">
                <Select
                  value={form.submission_source ?? ""}
                  onValueChange={(v) => set("submission_source", v)}
                >
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {SUBMISSION_SOURCES.map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            </div>
          </section>

          <section className="space-y-3">
            <SectionHeader title="Outcome" />
            <Field label="Status">
              {(() => {
                const future = isFutureDate(form.audition_date);
                const value = future ? "Upcoming" : form.outcome ?? "Not Yet Known";
                return (
                  <>
                    <Select
                      value={value}
                      onValueChange={(v) =>
                        set("outcome", v === "Not Yet Known" || v === "Upcoming" ? null : v)
                      }
                      disabled={future}
                    >
                      <SelectTrigger className={future ? "opacity-60" : undefined}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {OUTCOMES.filter((o) => future || o !== "Upcoming").map((o) => (
                          <SelectItem key={o} value={o}>{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {future && (
                      <p className="text-[11px] text-muted-foreground mt-1">
                        Locked as Upcoming until the audition date passes.
                      </p>
                    )}
                  </>
                );
              })()}
            </Field>
          </section>
        </div>

        <DialogFooter className="gap-2 sm:gap-2 mt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InsightsTabs({ auditions }: { auditions: Audition[] }) {
  const [sub, setSub] = useState<"insights" | "data">("insights");
  return (
    <Tabs value={sub} onValueChange={(v) => setSub(v as typeof sub)}>
      <TabsList>
        <TabsTrigger value="insights">
          <Sparkles className="size-4 mr-1.5" />
          Insights
        </TabsTrigger>
        <TabsTrigger value="data">
          <BarChart3 className="size-4 mr-1.5" />
          Data
        </TabsTrigger>
      </TabsList>
      <TabsContent value="insights" className="mt-6">
        <InsightsPanel auditions={auditions} />
      </TabsContent>
      <TabsContent value="data" className="mt-6">
        <DataPanel auditions={auditions} />
      </TabsContent>
    </Tabs>
  );
}

type InsightsPayload = {
  casting_narrative: string;
  your_type: string;
  whats_working: string;
  underused_range: string;
  recommendations: {
    top_priority: string[];
    secondary: string[];
    experimental: string[];
  };
  risks: string[];
  confidence: "low" | "medium" | "high";
  confidence_reason: string;
  meta?: {
    total: number;
    callbacks: { count: number; pct: number };
    booked: { count: number; pct: number };
    positive_rate: number;
  };
};

function InsightsPanel({ auditions }: { auditions: Audition[] }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  const [insights, setInsights] = useState<InsightsPayload | null>(null);
  const [insightId, setInsightId] = useState<string | null>(null);
  const [existingSnapshotId, setExistingSnapshotId] = useState<string | null>(null);
  const [auditionCountAtGen, setAuditionCountAtGen] = useState<number | null>(null);
  const [loadingExisting, setLoadingExisting] = useState(true);

  // Ask your coach
  const [askQuestion, setAskQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const [askedQuestion, setAskedQuestion] = useState("");
  const [askAnswer, setAskAnswer] = useState("");
  const [askError, setAskError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function handleAsk() {
    const q = askQuestion.trim();
    if (!q || asking) return;
    setAsking(true);
    setAskError(null);
    setAskAnswer("");
    setAskedQuestion(q);
    setAskOpen(true);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const invokePromise = supabase.functions.invoke("coach-ask", {
        body: { question: q, auditions },
      });
      const abortPromise = new Promise<never>((_, reject) => {
        controller.signal.addEventListener("abort", () =>
          reject(new Error("timeout")),
        );
      });
      const { data, error } = (await Promise.race([
        invokePromise,
        abortPromise,
      ])) as Awaited<typeof invokePromise>;
      clearTimeout(timeout);
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setAskAnswer(data?.answer ?? "");
      setAskQuestion("");
    } catch (err: any) {
      clearTimeout(timeout);
      setAskError(
        err?.message === "timeout"
          ? "Couldn't generate a response. Please try again."
          : "Couldn't generate a response. Please try again.",
      );
    } finally {
      setAsking(false);
    }
  }

  async function copyAnswer() {
    try {
      await navigator.clipboard.writeText(askAnswer);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy to clipboard.");
    }
  }


  // Load latest persisted insight
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoadingExisting(true);
      const { data, error } = await (supabase as any)
        .from("insights")
        .select("id, content, audition_count_at_generation")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      if (!error && data) {
        setInsights(data.content as InsightsPayload);
        setInsightId(data.id as string);
        setAuditionCountAtGen(data.audition_count_at_generation as number);
      }
      setLoadingExisting(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Check if a snapshot already exists for the current insight
  useEffect(() => {
    if (!insightId) {
      setExistingSnapshotId(null);
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await (supabase as any)
        .from("snapshots")
        .select("id")
        .eq("insight_id", insightId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (cancelled) return;
      setExistingSnapshotId(data?.id ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [insightId]);

  async function generate() {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "audition-insights",
        { body: { auditions } },
      );
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const payload = data as InsightsPayload;

      // Persist to insights table
      const { data: inserted, error: insertErr } = await (supabase as any)
        .from("insights")
        .insert({
          user_id: user.id,
          content: payload,
          audition_count_at_generation: auditions.length,
        })
        .select("id")
        .single();
      if (insertErr) throw insertErr;

      setInsights(payload);
      setInsightId(inserted?.id ?? null);
      setAuditionCountAtGen(auditions.length);
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to generate insights");
    } finally {
      setLoading(false);
    }
  }

  async function saveSnapshot() {
    if (!user || !insights) return;
    setSavingSnapshot(true);
    try {
      const stats = computeSnapshotStats(auditions);
      const { data, error } = await (supabase as any)
        .from("snapshots")
        .insert({
          user_id: user.id,
          insight_id: insightId,
          stats_json: stats,
          content: insights,
        })
        .select("id")
        .single();
      if (error) throw error;
      if (data?.id) setExistingSnapshotId(data.id);
      toast.success("Snapshot saved.", {
        action: {
          label: "View",
          onClick: () => {
            if (data?.id) window.location.href = `/snapshots/${data.id}`;
          },
        },
      });
    } catch (err: any) {
      toast.error(err?.message ?? "Couldn't save snapshot.");
    } finally {
      setSavingSnapshot(false);
    }
  }

  const lowData = auditions.length < 7;

  if (auditions.length < 3) {
    return (
      <Card className="p-10 text-center">
        <Sparkles className="size-6 text-muted-foreground mx-auto mb-3" />
        <h3 className="font-semibold tracking-tight">
          You're just getting started
        </h3>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-sm mx-auto">
          Log a few auditions and your coach will start spotting patterns in
          your type, what's landing, and where you're underused.
        </p>
        <p className="text-xs text-muted-foreground mt-3">
          {auditions.length} / 3 logged to unlock insights
        </p>
      </Card>
    );
  }

  const hasNewAuditions =
    auditionCountAtGen != null && auditions.length > auditionCountAtGen;
  const canRefresh = !insights || hasNewAuditions;
  const newCount =
    auditionCountAtGen != null ? auditions.length - auditionCountAtGen : 0;

  return (
    <div className="space-y-4">
      <Card className="p-5 flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-semibold tracking-tight">Your coach</h3>
          <p className="text-sm text-muted-foreground">
            Strategic insights based on your {auditions.length} logged
            auditions.
          </p>
          {insights && hasNewAuditions && (
            <p className="text-xs text-primary mt-2 flex items-center gap-1.5">
              <Sparkles className="size-3.5" />
              You've logged {newCount} new audition{newCount === 1 ? "" : "s"} —
              your insights are ready for a refresh.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {insights && existingSnapshotId && (
            <Button asChild variant="outline">
              <Link to="/snapshots/$id" params={{ id: existingSnapshotId }}>
                <FileText className="size-4 mr-1.5" />
                View Snapshot →
              </Link>
            </Button>
          )}
          {insights && !existingSnapshotId && (
            <Button
              variant="outline"
              onClick={saveSnapshot}
              disabled={savingSnapshot}
            >
              {savingSnapshot ? (
                <>
                  <Loader2 className="size-4 mr-1.5 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <FileText className="size-4 mr-1.5" />
                  Save as Snapshot
                </>
              )}
            </Button>
          )}
          <TooltipProvider>
            <UiTooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    onClick={generate}
                    disabled={loading || loadingExisting || !canRefresh}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="size-4 mr-1.5 animate-spin" />
                        Thinking…
                      </>
                    ) : (
                      <>
                        <Sparkles className="size-4 mr-1.5" />
                        {insights ? "Regenerate" : "Generate insights"}
                      </>
                    )}
                  </Button>
                </span>
              </TooltipTrigger>
              {insights && !canRefresh && (
                <TooltipContent>
                  Log a new audition to unlock updated insights.
                </TooltipContent>
              )}
            </UiTooltip>
          </TooltipProvider>
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="font-semibold tracking-tight">Ask your coach</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Ask a specific question about your career so far.
        </p>
        <form
          className="mt-4 flex gap-2 flex-col sm:flex-row"
          onSubmit={(e) => {
            e.preventDefault();
            handleAsk();
          }}
        >
          <Input
            value={askQuestion}
            onChange={(e) => setAskQuestion(e.target.value)}
            placeholder="e.g. Which casting directors do I resonate with most?"
            disabled={asking}
            className="flex-1"
          />
          <Button
            type="submit"
            disabled={asking || !askQuestion.trim()}
            style={{ backgroundColor: "#2a8891" }}
            className="text-white hover:opacity-90"
          >
            {asking ? (
              <>
                <Loader2 className="size-4 mr-1.5 animate-spin" />
                Asking…
              </>
            ) : (
              "Ask"
            )}
          </Button>
        </form>
      </Card>


      {lowData && (
        <Card className="p-4 border-dashed bg-muted/40">
          <p className="text-sm text-muted-foreground">
            <Sparkles className="size-3.5 inline mr-1.5 -mt-0.5" />
            Still early days — these insights will sharpen as you log more.
            Add a few more auditions to unlock deeper insights.
          </p>
        </Card>
      )}

      {insights && (
        <div className="space-y-4">
          {/* Casting narrative + confidence */}
          <Card className="p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                Casting narrative
              </h4>
              <ConfidenceBadge
                level={insights.confidence}
                reason={insights.confidence_reason}
              />
            </div>
            <p className="text-[15px] mt-3 leading-relaxed">
              {insights.casting_narrative}
            </p>
            {insights.meta && (
              <div className="flex gap-4 mt-4 text-xs text-muted-foreground flex-wrap">
                <span>
                  <strong className="text-foreground">{insights.meta.total}</strong>{" "}
                  auditions
                </span>
                <span>·</span>
                <span>
                  <strong className="text-foreground">
                    {insights.meta.callbacks.count}
                  </strong>{" "}
                  callbacks ({insights.meta.callbacks.pct}%)
                </span>
                <span>·</span>
                <span>
                  <strong className="text-foreground">
                    {insights.meta.booked.count}
                  </strong>{" "}
                  booked ({insights.meta.booked.pct}%)
                </span>
                <span>·</span>
                <span>
                  Positive rate:{" "}
                  <strong className="text-foreground">
                    {insights.meta.positive_rate}%
                  </strong>
                </span>
              </div>
            )}
          </Card>

          <div className="grid sm:grid-cols-2 gap-4">
            <InsightCard title="Your type" body={insights.your_type} />
            <InsightCard
              title="What's working"
              body={insights.whats_working}
            />
            <InsightCard
              title="Underused range"
              body={insights.underused_range}
            />
            <Card className="p-5">
              <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                Risks & trade-offs
              </h4>
              <ul className="mt-3 space-y-2">
                {insights.risks.map((r, i) => (
                  <li key={i} className="text-sm flex gap-2">
                    <span className="text-amber-600 dark:text-amber-400 mt-0.5">
                      ⚠
                    </span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          {/* Ranked recommendations */}
          <Card className="p-5">
            <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
              Recommended actions
            </h4>
            <div className="mt-4 space-y-4">
              <RecGroup
                tone="primary"
                label="Top priority"
                items={insights.recommendations.top_priority}
              />
              <RecGroup
                tone="secondary"
                label="Secondary"
                items={insights.recommendations.secondary}
              />
              <RecGroup
                tone="muted"
                label="Experimental"
                items={insights.recommendations.experimental}
              />
            </div>
          </Card>
        </div>
      )}

      <Dialog open={askOpen} onOpenChange={setAskOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-sm font-normal text-muted-foreground">
              {askedQuestion}
            </DialogTitle>
          </DialogHeader>
          <div className="min-h-[120px]">
            {asking ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-6">
                <Loader2 className="size-4 animate-spin" />
                Your coach is thinking…
              </div>
            ) : askError ? (
              <p className="text-sm text-destructive py-2">{askError}</p>
            ) : (
              <p className="text-[15px] leading-relaxed whitespace-pre-wrap">
                {askAnswer}
              </p>
            )}
          </div>
          {askAnswer && !asking && !askError && (
            <DialogFooter>
              <Button variant="outline" onClick={copyAnswer}>
                {copied ? (
                  <>
                    <Check className="size-4 mr-1.5" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="size-4 mr-1.5" />
                    Copy
                  </>
                )}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}



function ConfidenceBadge({
  level,
  reason,
}: {
  level: "low" | "medium" | "high";
  reason: string;
}) {
  const styles: Record<typeof level, string> = {
    low: "bg-muted text-muted-foreground border-border",
    medium:
      "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30",
    high: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  };
  return (
    <span
      title={reason}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-medium ${styles[level]}`}
    >
      <span className="size-1.5 rounded-full bg-current" />
      Confidence: {level}
    </span>
  );
}

function RecGroup({
  tone,
  label,
  items,
}: {
  tone: "primary" | "secondary" | "muted";
  label: string;
  items: string[];
}) {
  if (!items?.length) return null;
  const dot =
    tone === "primary"
      ? "bg-primary"
      : tone === "secondary"
        ? "bg-foreground/40"
        : "bg-muted-foreground/40";
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <span className={`size-1.5 rounded-full ${dot}`} />
        <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
          {label}
        </span>
      </div>
      <ul className="space-y-1.5 pl-4">
        {items.map((a, i) => (
          <li key={i} className="text-sm flex gap-2">
            <span className="text-muted-foreground mt-0.5">→</span>
            <span>{a}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function InsightCard({ title, body }: { title: string; body: string }) {
  return (
    <Card className="p-5">
      <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
        {title}
      </h4>
      <p className="text-sm mt-3 leading-relaxed whitespace-pre-line">
        {body}
      </p>
    </Card>
  );
}

function CallbackDialog({
  audition,
  onClose,
  onSave,
}: {
  audition: Audition | null;
  onClose: () => void;
  onSave: (date: string) => Promise<boolean>;
}) {
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (audition) {
      const base = audition.audition_date ? new Date(audition.audition_date) : new Date();
      const next = new Date(base);
      next.setDate(next.getDate() + 7);
      setDate(next);
      setSaving(false);
      setPickerOpen(false);
    } else {
      setDate(undefined);
      setSaving(false);
      setPickerOpen(false);
    }
  }, [audition]);

  if (!audition) return null;

  const handleSave = async () => {
    if (!date) return;
    setSaving(true);
    const ymd = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    const ok = await onSave(ymd);
    setSaving(false);
    if (!ok) return;
    onClose();
    toast("Callback logged.");
  };

  return (
    <Dialog open={!!audition} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md overflow-hidden">
        <DialogHeader>
          <DialogTitle>Callback for {audition.project_name}</DialogTitle>
          <DialogDescription>
            Another door opened. When are they bringing you back in?
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-2">
          <Label className="text-xs text-muted-foreground">Callback date</Label>
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !date && "text-muted-foreground",
                )}
              >
                <CalendarIcon className="size-4" />
                {date ? format(date, "PPP") : "Pick a date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <CalendarPicker
                mode="single"
                selected={date}
                onSelect={(d) => {
                  setDate(d);
                  if (d) setPickerOpen(false);
                }}
                initialFocus
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!date || saving}>
            {saving ? "Saving…" : "Save callback"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


type ChartInsights = {
  traits: string;
  funnel: string;
  formats: string;
  genres: string;
  ageRanges: string;
};

const MOMENTUM_TEAL = "#2a8891";

function MomentumChart({ auditions }: { auditions: Audition[] }) {
  const { series, change, topGenres, total } = useMemo(() => {
    const now = new Date();
    const startCurrent = new Date(now.getFullYear(), now.getMonth() - 5, 1);
    const startPrev = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    const endCurrent = new Date(now.getFullYear(), now.getMonth() + 1, 1);

    const monthlyMap = new Map<string, number>();
    let currentCount = 0;
    let prevCount = 0;
    const genreCounts = new Map<string, number>();

    for (const a of auditions) {
      const d = new Date(a.audition_date ?? a.created_at);
      if (isNaN(d.getTime())) continue;
      if (d >= startCurrent && d < endCurrent) {
        currentCount++;
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        monthlyMap.set(key, (monthlyMap.get(key) ?? 0) + 1);
        for (const g of a.genres ?? []) {
          if (!g) continue;
          genreCounts.set(g, (genreCounts.get(g) ?? 0) + 1);
        }
      } else if (d >= startPrev && d < startCurrent) {
        prevCount++;
      }
    }

    const series: { month: string; count: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      series.push({
        month: d.toLocaleString(undefined, { month: "short" }),
        count: monthlyMap.get(key) ?? 0,
      });
    }

    let change: number | null = null;
    if (prevCount > 0) {
      change = Math.round(((currentCount - prevCount) / prevCount) * 100);
    } else if (currentCount > 0) {
      change = 100;
    }

    const topGenres = Array.from(genreCounts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([name, count]) => ({
        name,
        count,
        pct: currentCount > 0 ? Math.round((count / currentCount) * 100) : 0,
      }));

    return { series, change, topGenres, total: currentCount };
  }, [auditions]);

  const positive = change !== null && change >= 0;
  const changeColor = change === null
    ? "text-muted-foreground"
    : positive
      ? "text-[color:var(--momentum-teal)]"
      : "text-red-500";

  return (
    <Card
      className="p-5"
      style={{ ["--momentum-teal" as string]: MOMENTUM_TEAL }}
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h4 className="text-sm font-semibold tracking-tight">
            Audition momentum
          </h4>
          <p className="text-xs text-muted-foreground mt-0.5">Last 6 months</p>
        </div>
        {change !== null && total > 0 && (
          <span
            className={cn(
              "text-sm font-semibold tabular-nums",
              changeColor,
            )}
          >
            {positive ? "+" : ""}
            {change}%
          </span>
        )}
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart
          data={series}
          margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
        >
          <defs>
            <linearGradient id="momentumFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={MOMENTUM_TEAL} stopOpacity={0.32} />
              <stop offset="100%" stopColor={MOMENTUM_TEAL} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="month"
            tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
            width={28}
          />
          <Tooltip contentStyle={tooltipStyle} />
          <Area
            type="monotone"
            dataKey="count"
            stroke={MOMENTUM_TEAL}
            strokeWidth={2}
            fill="url(#momentumFill)"
          />
        </AreaChart>
      </ResponsiveContainer>
      {topGenres.length > 0 && (
        <div className="mt-5 grid grid-cols-3 gap-2">
          {topGenres.map((g) => (
            <div
              key={g.name}
              className="rounded-lg border border-border/60 bg-muted/30 px-3 py-2.5"
            >
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold truncate">
                {g.name}
              </p>
              <p className="text-base font-semibold tracking-tight mt-1 tabular-nums">
                {g.pct}%
              </p>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}


function DataPanel({ auditions }: { auditions: Audition[] }) {
  const [insights, setInsights] = useState<ChartInsights | null>(null);
  const [loadingInsights, setLoadingInsights] = useState(false);

  const stats = useMemo(() => computeStats(auditions), [auditions]);

  useEffect(() => {
    if (auditions.length < 3) return;
    let cancelled = false;
    setLoadingInsights(true);
    setInsights(null);
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke(
          "data-insights",
          { body: { stats } },
        );
        if (cancelled) return;
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        setInsights(data);
      } catch (err: any) {
        if (!cancelled) {
          toast.error(err?.message ?? "Failed to load insights");
        }
      } finally {
        if (!cancelled) setLoadingInsights(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditions.length]);

  if (auditions.length < 7) {
    const remaining = Math.max(0, 7 - auditions.length);
    return (
      <Card className="p-10 text-center">
        <BarChart3 className="size-6 text-muted-foreground mx-auto mb-3" />
        <h3 className="font-semibold tracking-tight">
          {auditions.length === 0 ? "No data yet" : "Almost there"}
        </h3>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-sm mx-auto">
          {auditions.length === 0
            ? "Log your first audition to start seeing your data take shape."
            : "Add a few more auditions to unlock deeper insights — charts get meaningful around 7+ entries."}
        </p>
        {auditions.length > 0 && (
          <p className="text-xs text-muted-foreground mt-3">
            {auditions.length} logged · {remaining} to go
          </p>
        )}
      </Card>
    );
  }

  const PRIMARY = "var(--primary)";
  const MUTED_FG = "var(--muted-foreground)";
  const axisTick = { fill: MUTED_FG, fontSize: 11 };

  const traitData = toSorted(stats.traits);
  const formatData = toSorted(stats.formats);
  const genreData = toSorted(stats.genres);
  const AGE_ORDER = ["Under 18", "18-21", "22-25", "26-30", "31-35", "36-40", "40+"];
  const ageRangeData = AGE_ORDER
    .filter((a) => stats.ageRanges[a])
    .map((a) => ({ name: a, value: stats.ageRanges[a] }));
  const funnelData = [
    { name: "Submitted", value: stats.totals.total },
    { name: "Callback", value: stats.totals.callbacks },
    { name: "Booked", value: stats.totals.booked },
  ];

  const insightFor = (key: keyof ChartInsights) => {
    if (auditions.length < 3) {
      return "Log at least 3 auditions to unlock AI insights.";
    }
    if (loadingInsights) return "Analyzing your data…";
    return insights?.[key] ?? "Insight unavailable.";
  };

  return (
    <div className="space-y-4">
      <MomentumChart auditions={auditions} />

      <ChartCard
        title="Character traits"
        insight={insightFor("traits")}
        loading={loadingInsights}
      >
        <ResponsiveContainer
          width="100%"
          height={Math.max(220, traitData.length * 28)}
        >
          <BarChart
            data={traitData}
            layout="vertical"
            margin={{ top: 8, right: 16, left: 8, bottom: 4 }}
          >
            <XAxis
              type="number"
              tick={axisTick}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
            />
            <YAxis
              dataKey="name"
              type="category"
              tick={axisTick}
              axisLine={false}
              tickLine={false}
              width={90}
            />
            <Tooltip
              cursor={{ fill: "var(--muted)", opacity: 0.4 }}
              contentStyle={tooltipStyle}
            />
            <Bar dataKey="value" fill={PRIMARY} radius={[0, 4, 4, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard
        title="Conversion funnel"
        insight={insightFor("funnel")}
        loading={loadingInsights}
      >
        <ResponsiveContainer width="100%" height={240}>
          <BarChart
            data={funnelData}
            margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
          >
            <XAxis
              dataKey="name"
              tick={axisTick}
              axisLine={false}
              tickLine={false}
              interval={0}
            />
            <YAxis
              tick={axisTick}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
              width={28}
            />
            <Tooltip
              cursor={{ fill: "var(--muted)", opacity: 0.4 }}
              contentStyle={tooltipStyle}
            />
            <Bar dataKey="value" fill={PRIMARY} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard
        title="Project format"
        insight={insightFor("formats")}
        loading={loadingInsights}
      >
        <ResponsiveContainer width="100%" height={240}>
          <BarChart
            data={formatData}
            margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
          >
            <XAxis
              dataKey="name"
              tick={axisTick}
              axisLine={false}
              tickLine={false}
              interval={0}
            />
            <YAxis
              tick={axisTick}
              axisLine={false}
              tickLine={false}
              allowDecimals={false}
              width={28}
            />
            <Tooltip
              cursor={{ fill: "var(--muted)", opacity: 0.4 }}
              contentStyle={tooltipStyle}
            />
            <Bar dataKey="value" fill={PRIMARY} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard
        title="Project genre"
        insight={insightFor("genres")}
        loading={loadingInsights}
      >
        <ResponsiveContainer width="100%" height={240}>
          <BarChart
            data={genreData}
            margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
          >
            <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={0} />
            <YAxis tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} width={28} />
            <Tooltip cursor={{ fill: "var(--muted)", opacity: 0.4 }} contentStyle={tooltipStyle} />
            <Bar dataKey="value" fill={PRIMARY} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard
        title="Age range"
        insight={insightFor("ageRanges")}
        loading={loadingInsights}
      >
        <ResponsiveContainer width="100%" height={240}>
          <BarChart
            data={ageRangeData}
            margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
          >
            <XAxis dataKey="name" tick={axisTick} axisLine={false} tickLine={false} interval={0} />
            <YAxis tick={axisTick} axisLine={false} tickLine={false} allowDecimals={false} width={28} />
            <Tooltip cursor={{ fill: "var(--muted)", opacity: 0.4 }} contentStyle={tooltipStyle} />
            <Bar dataKey="value" fill={PRIMARY} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    </div>
  );
}

function toSorted(rec: Record<string, number>) {
  return Object.entries(rec)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
}

function computeStats(auditions: Audition[]) {
  const inc = (rec: Record<string, number>, key?: string | null) => {
    if (!key) return;
    rec[key] = (rec[key] ?? 0) + 1;
  };

  const traits: Record<string, number> = {};
  const trait_callbacks: Record<string, number> = {};
  const formats: Record<string, number> = {};
  const format_callbacks: Record<string, number> = {};
  const format_booked: Record<string, number> = {};
  const sources: Record<string, number> = {};
  const source_callbacks: Record<string, number> = {};
  const source_booked: Record<string, number> = {};
  const genres: Record<string, number> = {};
  const genre_callbacks: Record<string, number> = {};
  const genre_booked: Record<string, number> = {};
  const ageRanges: Record<string, number> = {};
  const ageRange_callbacks: Record<string, number> = {};
  const monthlyMap = new Map<string, number>();

  let callbacks = 0;
  let booked = 0;

  for (const a of auditions) {
    const isCallback = a.outcome === "Callback" || a.outcome === "Booked";
    const isBooked = a.outcome === "Booked";
    if (isCallback) callbacks++;
    if (isBooked) booked++;

    for (const t of a.traits ?? []) {
      inc(traits, t);
      if (isCallback) inc(trait_callbacks, t);
    }
    inc(formats, a.project_format);
    if (isCallback) inc(format_callbacks, a.project_format);
    if (isBooked) inc(format_booked, a.project_format);

    inc(sources, a.submission_source);
    if (isCallback) inc(source_callbacks, a.submission_source);
    if (isBooked) inc(source_booked, a.submission_source);

    for (const g of a.genres ?? []) {
      inc(genres, g);
      if (isCallback) inc(genre_callbacks, g);
      if (isBooked) inc(genre_booked, g);
    }

    const ar = bucketAgeRange(a.age_range);
    if (ar) {
      inc(ageRanges, ar);
      if (isCallback) inc(ageRange_callbacks, ar);
    }

    const d = new Date(a.audition_date ?? a.created_at);
    if (!isNaN(d.getTime())) {
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      monthlyMap.set(key, (monthlyMap.get(key) ?? 0) + 1);
    }
  }

  // Build last 6 months continuous range
  const now = new Date();
  const months: { month: string; count: number }[] = [];
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString(undefined, { month: "short" });
    months.push({ month: label, count: monthlyMap.get(key) ?? 0 });
  }

  return {
    totals: { total: auditions.length, callbacks, booked },
    traits,
    trait_callbacks,
    formats,
    format_callbacks,
    format_booked,
    sources,
    source_callbacks,
    source_booked,
    genres,
    genre_callbacks,
    genre_booked,
    ageRanges,
    ageRange_callbacks,
    monthly: months,
  };
}

const tooltipStyle = {
  background: "var(--card)",
  border: "1px solid var(--border)",
  borderRadius: 8,
  fontSize: 12,
  color: "var(--foreground)",
};

function ChartCard({
  title,
  insight,
  loading,
  children,
}: {
  title: string;
  insight?: string;
  loading?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Card className="p-5">
      <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-4">
        {title}
      </h4>
      {children}
      {insight !== undefined && (
        <div className="mt-4 pt-4 border-t flex gap-2 items-start">
          {loading ? (
            <Loader2 className="size-4 text-muted-foreground mt-0.5 animate-spin shrink-0" />
          ) : (
            <Sparkles className="size-4 text-primary mt-0.5 shrink-0" />
          )}
          <p className="text-sm leading-relaxed text-foreground/90">
            {insight}
          </p>
        </div>
      )}
    </Card>
  );
}

